/**
 * src/i18n/store.js
 *
 * Current-language state for the web-admin i18n foundation. 'en' | 'ar',
 * persisted to localStorage, with a tiny subscribe mechanism so any mounted
 * screen can re-render when the language changes (no framework here — this
 * app is vanilla ES modules + manual DOM, see src/screens/*.js).
 *
 * SCOPE NOTE: this file does not wire a language toggle into the shell —
 * src/shell/shell.js is owned by another agent in this run. Once a toggle
 * lands there, it should call setLang() from here; any screen that wants to
 * react live to a language change can addLangListener(...) or listen for the
 * 'staffly:langchange' window CustomEvent this module dispatches. Until that
 * toggle exists, the app always boots into whatever was last persisted (or
 * 'en' on first visit).
 *
 * Every browser-global reference (localStorage/document/window) is guarded
 * behind a typeof check so this module can also be imported from plain Node
 * (see web-admin/scripts/i18n-smoke.mjs) with no jsdom/DOM shim.
 */

import { loadDict, isDictLoaded } from './dicts.js';

const STORAGE_KEY = 'staffly.lang';
const SUPPORTED = ['en', 'ar'];
const DEFAULT_LANG = 'en';
const EVENT_NAME = 'staffly:langchange';

const hasLocalStorage = typeof localStorage !== 'undefined';
const hasDocument = typeof document !== 'undefined';
const hasWindow = typeof window !== 'undefined';

let _lang = readInitialLang();
const _listeners = new Set();

function readInitialLang() {
  if (hasLocalStorage) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
    } catch {
      // localStorage can throw in locked-down browser contexts (private-mode
      // quota, disabled storage, etc.) — fall through to the default rather
      // than fail app boot over a language preference.
    }
  }
  return DEFAULT_LANG;
}

/** Returns the active language code ('en' | 'ar'). */
export function getLang() {
  return _lang;
}

/** Returns 'rtl' for Arabic, 'ltr' for every other supported language. */
export function getDir() {
  return _lang === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Sets the active language, persists it to localStorage, stamps
 * <html lang/dir>, and notifies every subscriber — both the listener set
 * (addLangListener) and a 'staffly:langchange' window CustomEvent, so
 * callers can use whichever mechanism fits (a specific screen instance vs.
 * a global "re-render everything" bus).
 *
 * No-ops if `lang` is already active or unsupported-but-equal-to-current;
 * an unsupported value falls back to DEFAULT_LANG.
 */
export function setLang(lang) {
  const next = SUPPORTED.includes(lang) ? lang : DEFAULT_LANG;
  if (next === _lang) return;

  // Dictionaries are lazy (see dicts.js), so the incoming language's strings
  // may not be in memory yet. Flip only once they are: every listener below
  // re-renders synchronously, and re-rendering against an absent dictionary
  // would repaint the entire UI as raw dotted keys for the length of one
  // fetch. Awaiting first makes the toggle atomic — the language changes when
  // it can actually be displayed, never before.
  //
  // This keeps setLang's signature and its call sites unchanged (shell.js's
  // wireLangToggle still calls it fire-and-forget), at the cost of the flip
  // being asynchronous. After the first toggle the dictionary is cached, so
  // every subsequent flip is immediate; warmDict() in main.js pulls the other
  // language in after first paint so even the first toggle is usually instant.
  if (isDictLoaded(next)) {
    applyLang(next);
    return;
  }
  loadDict(next).then(
    () => applyLang(next),
    (err) => {
      // Keep the current language rather than switching to a UI of raw keys.
      // Loud, because a user pressed a button and nothing happened.
      console.error('[i18n] could not load dictionary for', next, err);
    },
  );
}

/** Commits a language change whose dictionary is confirmed resident: persist,
 * stamp <html lang/dir>, then notify every subscriber. Split out of setLang so
 * the cached and freshly-fetched paths share one implementation. */
function applyLang(next) {
  if (next === _lang) return;
  _lang = next;

  if (hasLocalStorage) {
    try {
      localStorage.setItem(STORAGE_KEY, _lang);
    } catch {
      // Non-fatal — same reasoning as readInitialLang().
    }
  }

  stampDocument();

  for (const listener of _listeners) {
    try {
      listener(_lang);
    } catch (err) {
      console.error('[i18n] language listener threw', err);
    }
  }

  if (hasWindow && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { lang: _lang } }));
  }
}

/**
 * Stamps document.documentElement.lang/dir from the current language. Called
 * automatically by setLang(); also exported so app boot (main.js) can apply
 * the persisted language once before any explicit setLang() call happens.
 */
export function stampDocument() {
  if (!hasDocument || !document.documentElement) return;
  document.documentElement.lang = _lang;
  document.documentElement.dir = getDir();
}

/**
 * Subscribes to language changes. Returns an unsubscribe function.
 * @param {(lang: string) => void} fn
 * @returns {() => void}
 */
export function addLangListener(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export const SUPPORTED_LANGS = SUPPORTED;
export const LANG_EVENT = EVENT_NAME;
